import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { GeneralService } from './general.service';
import { catchError, Observable, of, take } from 'rxjs';
import {
  ApiResponseTicket,
  PaginatedResponse,
  Ticket,
  TicketAttachment,
  TicketDetail,
  TicketTemplate,
  WorkFlowProject,
} from '../models/ticket-model/tiket.model';

@Injectable({
  providedIn: 'root',
})
export class TicketService {
  public URL_API = environment.apiURLNew;

  constructor(
    private http: HttpClient,
    private generalService: GeneralService,
  ) {}

  public getWorkFlowProjects(): Observable<ApiResponseTicket<WorkFlowProject[]> | null> {
    return this.http
      .get<ApiResponseTicket<WorkFlowProject[]>>(this.URL_API + `/api/WorkFlowProject/List`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public ticketSearch(params: {
    SearchField: string;
    SearchFieldValue: string;
    Page: number;
    PageSize: number;
  }): Observable<ApiResponseTicket<PaginatedResponse<Ticket>> | null> {
    const paramsAux = this.generalService.buildParams(params);
    return this.http
      .get<
        ApiResponseTicket<PaginatedResponse<Ticket>>
      >(this.URL_API + `/api/TicketSearch/Paged`, { params: paramsAux })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public openTicket(params: {
    Page: number;
    PageSize: number;
    SearchField?: string;
    SearchFieldValue?: string;
    Sort?: string;
    SortDir?: string;
  }): Observable<ApiResponseTicket<PaginatedResponse<Ticket>> | null> {
    const paramsAux = this.generalService.buildParams(params);
    return this.http
      .get<
        ApiResponseTicket<PaginatedResponse<Ticket>>
      >(this.URL_API + `/api/OpenTicket/Paged`, { params: paramsAux })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public closeTicket(params: {
    Page: number;
    PageSize: number;
    SearchField?: string;
    SearchFieldValue?: string;
    Sort?: string;
    SortDir?: string;
  }): Observable<ApiResponseTicket<PaginatedResponse<Ticket>> | null> {
    const paramsAux = this.generalService.buildParams(params);
    return this.http
      .get<
        ApiResponseTicket<PaginatedResponse<Ticket>>
      >(this.URL_API + `/api/ClosedTicket/Paged`, { params: paramsAux })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getTicketDetail(id: number | string): Observable<ApiResponseTicket<TicketDetail> | null> {
    return this.http
      .get<ApiResponseTicket<TicketDetail>>(this.URL_API + `/api/Ticket/Detail/${id}`)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getTicketComments(id: number | string): Observable<ApiResponseTicket<any> | null> {
    return this.http
      .get<ApiResponseTicket<any>>(this.URL_API + `/api/TicketComment/List?ticketId=${id}`)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getTicketTemplate(params: {
    SearchField: string;
    SearchFieldValue: string;
    Page: number;
    PageSize: number;
  }): Observable<ApiResponseTicket<PaginatedResponse<TicketTemplate>> | null> {
    const paramsAux = this.generalService.buildParams(params);
    return this.http
      .get<
        ApiResponseTicket<PaginatedResponse<TicketTemplate>>
      >(this.URL_API + `/api/TicketTemplate/Paged`, { params: paramsAux })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public createTicketComment(body: {
    CommentTypeId: number;
    TicketId: number;
    Comment: string;
    CurrentDate: string;
  }): Observable<ApiResponseTicket<any> | null> {
    return this.http
      .post<ApiResponseTicket<any>>(this.URL_API + `/api/TicketComment/Create`, body)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public createTicketTemplate(body: {
    Description: string;
    Content: string;
    TemplateId: number | null;
    TicketId: number;
    Username: string;
  }): Observable<ApiResponseTicket<any> | null> {
    return this.http
      .post<ApiResponseTicket<any>>(this.URL_API + `/api/TicketTemplate/Create`, body)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public createTicketAttachment(formData: {
    TicketId: number;
    FileName: string;
    FileContent: string;
  }): Observable<ApiResponseTicket<any> | null> {
    const formDataAux: FormData = new FormData();
    formDataAux.append('TicketId', formData.TicketId.toString());
    formDataAux.append('FileName', formData.FileName);
    formDataAux.append('FileContent', formData.FileContent);
    return this.http
      .post<ApiResponseTicket<any>>(this.URL_API + `/api/TicketAttachment/Create`, formDataAux)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getFile(url: string): Observable<ApiResponseTicket<TicketAttachment> | null> {
    return this.http.get<ApiResponseTicket<TicketAttachment> | null>(this.URL_API + `/${url}`).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public ticketCreate(body: {
    CustomerId: number;
    WorkFlowId: number | string;
    LocationId: number | string;
    SerialNumber: string;
    WorkFlowEditFields: any[];
    PostalCodeId: number | string;
    StreetName: string;
    StreetNumber: string;
    ApartmentNumber: string;
    POCFullName: string;
    POCPhoneNumber: string;
    POCMobileNumber: string;
    POCEmail: string;
    Project: string;
    SourceTypeId?: number;
  }): Observable<any | null> {
    const payload = {
      SourceTypeId: 2,
      ...body,
    };
    return this.http.post<any>(`${this.URL_API}/api/WorkFlowProject/Create`, payload).pipe(
      catchError(() => {
        return of(null);
      }),
    );
  }
}
